import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CtsReviewCadence, CtsReviewPeriodicCadence } from '@grc/shared';
import { apiClient } from '@/api/client';
import { useAuthStore } from '@/store/auth.store';
import type { HubReview, ReviewSettings, ReviewSummary } from '@/api/types/cts-workspace.types';

// ── CAPR S3 — the review cadence: the organization default and the per-control override ────────
//
// ⚠ A CADENCE CHANGE MOVES EVERY DATE AND LABEL THAT DEPENDS ON IT (BR-C7), so each write invalidates
//   the whole workspace — the list and every hub — rather than patching one cached row. The server
//   recomputes; nothing here does date arithmetic.

export function useReviewSettings() {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['cts-review-settings', tenantId],
    queryFn: async () => {
      const { data } = await apiClient.get('/compliance/review-settings');
      return data.data as ReviewSettings;
    },
    enabled: !!tenantId,
  });
}

/**
 * The organization review settings — the review default and/or (CAPR S5) whether a different approver
 * is required. Send only what changes; the server changes both together or neither.
 */
export function useUpdateReviewSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { review_cadence?: CtsReviewPeriodicCadence; require_different_approver?: boolean }) => {
      const { data } = await apiClient.patch('/compliance/review-settings', v);
      return data.data as { review_cadence: CtsReviewPeriodicCadence; require_different_approver: boolean; changed: boolean };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cts-review-settings'] });
      qc.invalidateQueries({ queryKey: ['cts-workspace'] });
    },
  });
}

/**
 * ⚠ CAPR S5 — the Controls list counted by review label, for the Home band. Keyed UNDER 'cts-workspace' on
 * purpose: every write that moves a label (a cadence change, a decision, a start) already invalidates that
 * prefix, so the counts can never lag the list they summarise.
 */
export function useReviewSummary(enabled = true) {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['cts-workspace', 'review-summary', tenantId],
    queryFn: async () => {
      const { data } = await apiClient.get('/compliance/workspace/review-summary');
      return data.data as ReviewSummary;
    },
    enabled: !!tenantId && enabled,
  });
}

/** CAPR S5 — the server's per-control answer to a bulk cadence change. Every outcome is named. */
export interface BulkCadenceResult {
  outcome: 'all_applied' | 'partial' | 'none_applied';
  changed: string[];
  unchanged: string[];
  skipped: { reference: string; reason: string }[];
  failed: { reference: string; reason: string }[];
}

/** CAPR S5 — set (or clear) the review cadence on each selected control: the single override, per control. */
export function useBulkSetReviewCadence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { items: { controlReference: string; cadence: CtsReviewCadence | null; rationale?: string }[] }) => {
      const { data } = await apiClient.post('/compliance/workspace/controls/bulk-review-cadence', v);
      return data.data as BulkCadenceResult;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cts-workspace'] });
    },
  });
}

/** Set (`cadence` + `rationale`) or clear (`cadence: null`) one control's override. */
export function useSetControlReviewCadence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { controlReference: string; cadence: CtsReviewCadence | null; rationale?: string }) => {
      const { data } = await apiClient.post(
        `/compliance/workspace/controls/${v.controlReference}/review-cadence`,
        v.cadence === null ? { cadence: null } : { cadence: v.cadence, rationale: v.rationale },
      );
      return data.data as { changed: boolean; review: HubReview };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cts-workspace'] });
    },
  });
}
